/**
 * Scale handle specifically for OdDbBlockReference entities.
 *
 * ODA's transformBy() does not support scaling for BlockReferences properly.
 * This handle uses setScaleFactors() and setPosition() directly instead.
 *
 * Provides uniform scaling (same scale on X and Y axes).
 *
 * Performance optimization: Uses the ODA view.add() pattern from BaseHandle.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, KeyPoints, Point3D } from "../types";
import { BaseHandle } from "./BaseHandle";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
  HANDLE_OFFSET_PIXELS,
} from "../types";
import { deleteObj } from "../../core/delete";
import { openAs } from "../../core/oda";
import { distance2D } from "../matrix";
import {
  BlockReferenceTransformCommand,
  blockRefStateChanged,
  type BlockRefState,
  type BlockRefStateParams,
} from "../../commands/BlockReferenceTransformCommand";
import { applyDeltaScaleWithAnchorPoint } from "@/drawing/blocks/transform";

/**
 * Handle for uniform scaling BlockReference entities.
 * Uses setScaleFactors() and setPosition() instead of transformBy().
 */
export class BlockReferenceScaleHandle extends BaseHandle {
  readonly type = HandleType.Scale;
  readonly position: HandlePosition;

  /** The anchor point (opposite corner) for scaling */
  private anchorPoint: Point3D;

  /** Initial distance from anchor to handle */
  private initialDistance: number;

  /** scale handles does not snap to anything */
  snapOptions = { acceptOrtho: false, acceptPolar: false, acceptObject: false };

  /** Original state captured at start (for undo) */
  private originalState: BlockRefState | null = null;

  /** Previous scale value for incremental updates */
  private prevUniformScale = 1;

  private diagonalOffsetInPx =
    (HANDLE_OFFSET_PIXELS * HANDLE_SIZES.handleScale) / Math.sqrt(2);
  private diagonalWorldOffset: number;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
    position: HandlePosition,
  ) {
    super(viewer, history, entityIds, keyPoints, position);

    this.position = position;

    this.diagonalWorldOffset = this.viewer.screenDistanceToWorld(
      this.diagonalOffsetInPx,
    );

    this.worldPosition = {
      x: this.keyPoints.max.x + this.diagonalWorldOffset,
      y: this.keyPoints.min.y - this.diagonalWorldOffset,
      z: 0,
    };

    // Set anchor to opposite corner (topLeft for bottom-right positioned handle)
    this.anchorPoint = this.getOppositeCorner(position);

    // Calculate initial distance
    this.initialDistance = distance2D(this.anchorPoint, this.worldPosition);

    // Override draw options for scale handle
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.scaleHandle,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.scale * HANDLE_SIZES.handleScale,
      icon: "scale",
    };
  }

  beforeRedraw(): void {
    const previousDiagonalWorldOffset = this.diagonalWorldOffset;
    this.diagonalWorldOffset = this.viewer.screenDistanceToWorld(
      this.diagonalOffsetInPx,
    );
    this.worldPosition.x +=
      this.diagonalWorldOffset - previousDiagonalWorldOffset;
    this.worldPosition.y += -(
      this.diagonalWorldOffset - previousDiagonalWorldOffset
    );
  }

  /**
   * Get the opposite corner for anchoring.
   */
  private getOppositeCorner(position: HandlePosition): Point3D {
    switch (position) {
      case HandlePosition.TopLeft:
        return this.keyPoints.bottomRight;
      case HandlePosition.TopRight:
        return this.keyPoints.bottomLeft;
      case HandlePosition.BottomLeft:
        return this.keyPoints.topRight;
      case HandlePosition.BottomRight:
        return this.keyPoints.topLeft;
      default:
        return this.keyPoints.center;
    }
  }

  /**
   * Start scale - capture original BlockReference state.
   */
  start(): void {
    super.start();
    // Reset scale tracking
    this.prevUniformScale = 1;

    // Capture original state for the BlockReference
    if (this.entityIds.length > 0) {
      const entity = openAs(this.entityIds[0], this.module.OdDbEntity, 0);
      const blockRef = this.module.OdDbBlockReference.cast(entity);

      if (blockRef) {
        const scale = blockRef.scaleFactors();
        const pos = blockRef.position();

        this.originalState = {
          scaleFactors: [scale.sx, scale.sy, scale.sz],
          position: [pos.x, pos.y, pos.z],
        };

        deleteObj(scale);
        deleteObj(pos);
      }
      deleteObj(entity);
    }
  }

  /**
   * Update scale during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Calculate current distance from anchor to cursor
    const currentDistance = distance2D(this.anchorPoint, context.worldPoint);

    // Calculate scale ratio (uniform scaling)
    let scale = currentDistance / this.initialDistance;

    // Clamp scale to reasonable values
    scale = Math.max(0.1, Math.min(10, scale));

    // Snap to common scales if Shift is held
    if (context.shiftKey) {
      const snapValues = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0, 4.0];
      scale = snapValues.reduce((prev, curr) =>
        Math.abs(curr - scale) < Math.abs(prev - scale) ? curr : prev,
      );
    }

    // Apply scale using BlockReference-specific methods
    this.applyBlockReferenceScale(scale);

    // Update view with throttling for better performance
    this.throttledUpdate();
  }

  /**
   * Apply uniform scale to BlockReference using setScaleFactors and setPosition.
   * Uses ghost entity if available (for performance), otherwise original.
   */
  private applyBlockReferenceScale(scale: number): void {
    // Compute delta scale from previous
    const deltaScale =
      this.prevUniformScale !== 0 ? scale / this.prevUniformScale : scale;

    for (const data of this.transformData) {
      // Use ghost entity if available (fast path), otherwise fall back to original
      const entity =
        data.ghostEntity ?? openAs(data.entityId, this.module.OdDbEntity, 1);
      const isGhost = !!data.ghostEntity;
      const blockRef = this.module.OdDbBlockReference.cast(entity);

      if (blockRef) {
        applyDeltaScaleWithAnchorPoint(
          this.module,
          blockRef,
          this.anchorPoint,
          deltaScale,
          deltaScale,
        );
      }

      // PERFORMANCE: Only call recordGraphicsModified for non-ghost entities
      if (!isGhost) {
        entity.recordGraphicsModified(true);
        deleteObj(entity);
      }
    }

    // Update previous value
    this.prevUniformScale = scale;
  }

  /**
   * End scale - execute BlockReference-specific command.
   */
  end(): void {
    if (!this.isTransforming) return;

    this.isTransforming = false;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // Get final state from ghost (if using ghosts) or original
    let newState: BlockRefState | null = null;

    // Clean up ghosts and apply final state to originals
    const entityHandlesForLog = this.transformData
      .map((d) => d.handle)
      .join(",");
    const ghostsToDelete: DrawingModule.OdDbEntity[] = [];
    for (const data of this.transformData) {
      if (this.useViewGhosts && data.ghostEntity && view) {
        // Get final state from ghost
        const ghostBlockRef = this.module.OdDbBlockReference.cast(
          data.ghostEntity,
        );
        if (ghostBlockRef && !newState) {
          const scale = ghostBlockRef.scaleFactors();
          const pos = ghostBlockRef.position();
          newState = {
            scaleFactors: [scale.sx, scale.sy, scale.sz],
            position: [pos.x, pos.y, pos.z],
          };
          deleteObj(scale);
          deleteObj(pos);
        }

        // Remove ghost from view
        view.erase(data.ghostEntity);
        ghostsToDelete.push(data.ghostEntity);
        data.ghostEntity = undefined;

        // Apply final state to original
        if (newState) {
          const originalEntity = openAs(
            data.entityId,
            this.module.OdDbEntity,
            1,
          );
          const originalBlockRef =
            this.module.OdDbBlockReference.cast(originalEntity);
          if (originalBlockRef) {
            const scale = new this.module.OdGeScale3d(
              newState.scaleFactors[0],
              newState.scaleFactors[1],
              newState.scaleFactors[2],
            );
            const pos = new this.module.OdGePoint3d(
              newState.position[0],
              newState.position[1],
              newState.position[2],
            );
            originalBlockRef.setScaleFactors(scale);
            originalBlockRef.setPosition(pos);
            deleteObj(scale);
            deleteObj(pos);
          }
          originalEntity.recordGraphicsModified(true);
          deleteObj(originalEntity);
        }
      } else {
        // Non-ghost path: get state from original
        const entity = openAs(data.entityId, this.module.OdDbEntity, 0);
        const blockRef = this.module.OdDbBlockReference.cast(entity);
        if (blockRef && !newState) {
          const scale = blockRef.scaleFactors();
          const pos = blockRef.position();
          newState = {
            scaleFactors: [scale.sx, scale.sy, scale.sz],
            position: [pos.x, pos.y, pos.z],
          };
          deleteObj(scale);
          deleteObj(pos);
        }
        deleteObj(entity);
      }
    }

    // Undo/redo replays absolute placements, not an invertible transform: a
    // BlockReference is scaled by writing setScaleFactors()/setPosition().
    const before = this.originalState;
    const after = newState;
    const params: BlockRefStateParams[] =
      before && after
        ? this.transformData.map((data) => ({
            handle: data.handle,
            before,
            after,
          }))
        : [];

    // Clear state
    this.transformData = [];
    this.originalState = null;

    // Execute command if there was actual change
    if (blockRefStateChanged(params)) {
      const command = new BlockReferenceTransformCommand(this.viewer, params);
      this.history.execute(command);
    }

    device?.invalidate();
    try {
      this.safeViewerUpdate("BlockReferenceScaleHandle.end", {
        entityHandles: entityHandlesForLog,
      });
    } finally {
      for (const ghost of ghostsToDelete) deleteObj(ghost);
    }
  }

  /**
   * Cancel scale - restore original BlockReference state.
   */
  cancel(): void {
    if (!this.isTransforming) return;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // Clean up ghosts (originals were never modified, so no restore needed)
    const entityHandlesForLog = this.transformData
      .map((d) => d.handle)
      .join(",");
    const ghostsToDelete: DrawingModule.OdDbEntity[] = [];
    for (const data of this.transformData) {
      if (this.useViewGhosts && data.ghostEntity && view) {
        // Remove ghost from view
        view.erase(data.ghostEntity);
        ghostsToDelete.push(data.ghostEntity);
        data.ghostEntity = undefined;
      } else {
        // Non-ghost path: restore original state
        if (this.originalState) {
          const entity = openAs(data.entityId, this.module.OdDbEntity, 1);
          const blockRef = this.module.OdDbBlockReference.cast(entity);

          if (blockRef) {
            const scale = new this.module.OdGeScale3d(
              this.originalState.scaleFactors[0],
              this.originalState.scaleFactors[1],
              this.originalState.scaleFactors[2],
            );
            const pos = new this.module.OdGePoint3d(
              this.originalState.position[0],
              this.originalState.position[1],
              this.originalState.position[2],
            );

            blockRef.setScaleFactors(scale);
            blockRef.setPosition(pos);

            deleteObj(scale);
            deleteObj(pos);
          }

          entity.recordGraphicsModified(true);
          deleteObj(entity);
        }
      }
    }

    // Reset state
    this.prevUniformScale = 1;
    this.originalState = null;

    this.isTransforming = false;
    this.transformData = [];

    device?.invalidate();
    try {
      this.safeViewerUpdate("BlockReferenceScaleHandle.cancel", {
        entityHandles: entityHandlesForLog,
      });
    } finally {
      for (const ghost of ghostsToDelete) deleteObj(ghost);
    }
  }
}
